import { Injectable } from '@angular/core';
import { ActivatedRoute, Router, UrlTree } from '@angular/router';
import type { FilterCacheFindInterface } from '@zaoblako/shared/modules/personalization/dtos/filter-cache/filter-cache.find.dto';
import type { FilterDigestFindInterface } from '@zaoblako/shared/modules/personalization/dtos/filter-digest/filter-digest-find.dto';
import { FilterCacheKey } from '@zaoblako/shared/modules/personalization/enums/filter-cache-key.enum';
import { ReportTypeEnum } from '@zaoblako/shared/modules/personalization/enums/report.type.enum';
import { SerdeDtoClass } from '@zaoblako/shared/modules/serde/classes/serde-dto.class';
import { GlobalService } from '@zaoblako/web/core/services/global.service';
import { FilterCacheService } from '@zaoblako/web/core/services/personalization/filter-cache.service';
import { FilterDigestService } from '@zaoblako/web/core/services/personalization/filter-digest.service';
import { plainToClass } from 'class-transformer';
import { flatten, unflatten } from 'flat';
import { forkJoin, from, map, Observable, of, switchMap, tap } from 'rxjs';
import { catchError } from 'rxjs/operators';

const paramsMap = {
  clientIds: 'clients:array:id',
  directionIds: 'directions:hierarchy:id',
  from: 'from:string',
  limit: 'limit:number',
  stepping: 'stepping:boolean',
  offset: 'offset:number',
  responsibleIds: 'responsible:array:id',
  search: 'search:string',
  sort: 'sort:string',
  squareMax: 'squareMax:number',
  squareMin: 'squareMin:number',
  statusIds: 'statuses:array:id',
  groupIds: 'groups:array:id',
  tagIds: 'tags:array:id',
  to: 'to:string',
  types: 'types:array',
  year: 'year:string',
  userIds: 'users:array:id',
  taskStatusIds: 'taskStatuses:array:id',
  budgetStatusIds: 'budgetStatuses:array:id',
  brsTaskStatusIds: 'brsTaskStatuses:array:id',
  operatorIds: 'operators:array:id',
  bankIds: 'banks:array:id',
  formIds: 'forms:array:id',
  filialIds: 'filials:array:id',
  unitIds: 'units:array:id',
  contragentIds: 'contragents:array:id',
  companyIds: 'companies:array:id',
  documentTypeIds: 'documentTypes:array:id',
  method: 'methods:array',
  employeeIds: 'employees:array:id',
  masterMilestoneIds: 'masterMilestones:array:id',
  creatorIds: 'creators:array:id',
  dealIds: 'deals:array:id',
  ndsIds: 'nds:array:id',
  vatIds: 'vats:array:id',
  operationTypeIds: 'operationTypes:array:id',
  documentTypeCodes: 'documentTypes:array:code',
  providerIds: 'providers:array:id',
  categoryIds: 'categories:array:id',
  planNameIds: 'planNames:array:id',
  brandIds: 'brands:array:id',
  rangeIds: 'ranges:array:id',
  priorityIds: 'priorities:array:id',
  templateIds: 'templates:array:id',
  bankDetailsIds: 'bankDetails:array:id',
  contractIds: 'contracts:array:id',
  roomIds: 'rooms:array:id',
  connectionIds: 'connections:array:id'
};

@Injectable({
  providedIn: 'root'
})
export class QueryParamsService {
  public constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly globalService: GlobalService,
    private readonly filterCacheService: FilterCacheService,
    private readonly filterDigestService: FilterDigestService
  ) {}

  public getFilteredQueryParams<T>(
    dto: (new () => T) | SerdeDtoClass<T>,
    params: {
      required?: Partial<Record<keyof T, any>>;
      optional?: Partial<Record<keyof T, any>>;
      filterType?: ReportTypeEnum;
      filterCacheType?: FilterCacheKey;
      sessionStorageName?: string;
    }
  ): Observable<{ filters: T; select?: string[] }> {
    const result: T =
      dto instanceof SerdeDtoClass
        ? (Object.fromEntries(Object.keys(dto.getMetadata()).map((key) => [key, undefined])) as T)
        : plainToClass(dto, {});
    const queryParams = unflatten(this.route.snapshot.queryParams);
    const filterTypeStorage: Record<keyof T, any> = this.globalService.globalInfo.savedFilters?.find(
      (savedFilter) => savedFilter.report === params?.filterType && savedFilter.isFavorite
    )?.filter;
    const redirectStorage: Record<keyof T, any> = JSON.parse(
      sessionStorage.getItem(`${params.sessionStorageName}.redirect.queryParams`)
    );

    const parseParams = (filterCache?: FilterCacheFindInterface<T>): T => {
      Object.keys(result).forEach((key) => {
        if (params?.required?.hasOwnProperty(key)) {
          result[key] = params.required[key];
        }

        if (redirectStorage) {
          if (key === 'offset') {
            result[key] = 0;
            return;
          }

          if (key === 'search') {
            delete result[key];
            return;
          }

          if (redirectStorage[key]) {
            result[key] = redirectStorage[key];
          }
          return;
        }

        if (Object.keys(queryParams)?.length) {
          if (key === 'offset') {
            result[key] = 0;
            return;
          }

          if (key === 'search') {
            delete result[key];
            return;
          }

          const type = paramsMap[key] && paramsMap[key].includes(':') ? paramsMap[key].split(':')[1] : '';

          if (queryParams[key]) {
            result[key] = queryParams[key];

            if (type === 'number') {
              result[key] = +result[key];
            } else if (type === 'boolean' || ['true', 'false'].includes(String(result[key]).toLowerCase())) {
              result[key] = String(result[key]).toLowerCase() === 'true';
            }
          }
        } else {
          if (!result[key] && params?.optional?.[key] !== undefined && params?.optional?.[key] !== null) {
            result[key] = params.optional[key];
          }
          if (filterTypeStorage) {
            if (filterTypeStorage?.[key]) {
              result[key] = filterTypeStorage[key];
            }
          } else if (filterCache?.filters) {
            if (filterCache?.filters?.[key]) {
              result[key] = filterCache.filters[key];
            }
          }
        }

        if (
          result[key] == undefined ||
          Number.isNaN(result[key]) ||
          result[key] === [undefined] ||
          result[key] === '[object Object]' ||
          result[key] === '{}' ||
          (typeof result[key] === 'object' && !Object.keys(result[key]).length)
        ) {
          delete result[key];
        }
      });

      if (redirectStorage) {
        sessionStorage.removeItem(`${params.sessionStorageName}.redirect.queryParams`);
      }

      return result;
    };

    if (params.filterCacheType) {
      return this.filterCacheService
        .findOneByKey<T>(params.filterCacheType)
        .pipe(map((filterCache) => ({ filters: parseParams(filterCache), select: filterCache.select })));
    } else {
      return of({ filters: parseParams() });
    }
  }

  public setFilterCache<T>(filterCacheType: FilterCacheKey, filters: T, select?: string[]) {
    this.filterCacheService.save(filterCacheType, { filters, select }).subscribe();
  }

  public setQueryParams(
    queryParams: Record<string, any>,
    params: {
      isNotSave?: boolean;
      filterCacheType?: FilterCacheKey;
      select?: string[];
      noScrollingToTop?: boolean;
      notSaveFields?: string[];
      notDeleteSort?: boolean;
    }
  ) {
    const filteredParams = flatten({ ...queryParams }) as Record<string, any>;

    delete filteredParams.limit;
    delete filteredParams.offset;
    delete filteredParams.search;
    delete filteredParams.filtersCount;

    if (!params.notDeleteSort) {
      delete filteredParams.sort;
    }

    Object.keys(filteredParams).forEach((key) => {
      if (
        filteredParams[key] == undefined ||
        Number.isNaN(filteredParams[key]) ||
        filteredParams[key] === [undefined] ||
        filteredParams[key] === '[object Object]' ||
        filteredParams[key] === '{}' ||
        (typeof filteredParams[key] === 'object' && !Object.keys(filteredParams[key]).length)
      ) {
        delete filteredParams[key];
      }
    });

    if (params?.filterCacheType && !params?.isNotSave) {
      if (params?.notSaveFields?.length) {
        const savedFilter = { ...queryParams };
        Object.keys(savedFilter).forEach((key) => {
          if (params?.notSaveFields?.includes(key)) {
            delete savedFilter[key];
          }
        });
        this.setFilterCache(params?.filterCacheType, savedFilter, params?.select);
      } else {
        this.setFilterCache(params?.filterCacheType, queryParams, params?.select);
      }
    }

    const currentScrollHeight = window.scrollY;

    this.router
      .navigate([], {
        relativeTo: this.route,
        queryParams: filteredParams,
        replaceUrl: true
      })
      .then(() => {
        if (params?.noScrollingToTop) {
          window.scrollTo({ top: currentScrollHeight });
        }
      })
      .catch(console.error);
  }

  public getFilteredQueryParamsV2<T>(
    dto: (new () => T) | SerdeDtoClass<T>,
    params: {
      required?: Partial<Record<keyof T, any>>;
      optional?: Partial<Record<keyof T, any>>;
      filterType?: ReportTypeEnum;
      filterCacheType?: FilterCacheKey;
      sessionStorageName?: string;
    }
  ): Observable<{ filters: T; select?: string[] }> {
    const result: T =
      dto instanceof SerdeDtoClass
        ? (Object.fromEntries(Object.keys(dto.getMetadata()).map((key) => [key, undefined])) as T)
        : plainToClass(dto, {});
    const filterTypeStorage: Record<keyof T, any> = this.globalService.globalInfo.savedFilters?.find(
      (savedFilter) => savedFilter.report === params?.filterType && savedFilter.isFavorite
    )?.filter;
    const redirectStorage: Record<keyof T, any> = JSON.parse(
      sessionStorage.getItem(`${params.sessionStorageName}.redirect.queryParams`)
    );
    const digest = unflatten(this.route.snapshot.queryParams)?.['digest'];
    let filterParams = {};

    const parseParams = (filterCache?: FilterCacheFindInterface<T>): T => {
      Object.keys(result).forEach((key) => {
        if (params?.required?.hasOwnProperty(key)) {
          result[key] = params.required[key];
        }

        if (redirectStorage) {
          if (key === 'offset') {
            result[key] = 0;
            return;
          }

          if (key === 'search') {
            delete result[key];
            return;
          }

          if (redirectStorage[key]) {
            result[key] = redirectStorage[key];
          }
          return;
        }

        if (filterParams && Object.keys(filterParams)?.length) {
          if (key === 'offset') {
            result[key] = 0;
            return;
          }

          if (key === 'search') {
            delete result[key];
            return;
          }

          const type = paramsMap[key] && paramsMap[key].includes(':') ? paramsMap[key].split(':')[1] : '';

          if (filterParams[key]) {
            result[key] = filterParams[key];

            if (type === 'number') {
              result[key] = +result[key];
            } else if (type === 'boolean' || ['true', 'false'].includes(String(result[key]).toLowerCase())) {
              result[key] = String(result[key]).toLowerCase() === 'true';
            }
          }
        } else {
          if (!result[key] && params?.optional?.[key] !== undefined && params?.optional?.[key] !== null) {
            result[key] = params.optional[key];
          }
          if (filterTypeStorage) {
            if (filterTypeStorage?.[key]) {
              result[key] = filterTypeStorage[key];
            }
          } else if (filterCache?.filters) {
            if (filterCache?.filters?.[key]) {
              result[key] = filterCache.filters[key];
            }
          }
        }

        if (
          result[key] == undefined ||
          Number.isNaN(result[key]) ||
          result[key] === [undefined] ||
          result[key] === '[object Object]' ||
          result[key] === '{}' ||
          (typeof result[key] === 'object' && !Object.keys(result[key]).length)
        ) {
          delete result[key];
        }
      });

      if (redirectStorage) {
        sessionStorage.removeItem(`${params.sessionStorageName}.redirect.queryParams`);
      }

      return result;
    };

    if (params.filterCacheType && !digest) {
      return this.filterCacheService
        .findOneByKey<T>(params.filterCacheType)
        .pipe(
          switchMap((filterCache: FilterCacheFindInterface<T>) =>
            this.filterDigestService
              .createOne({ filters: parseParams(filterCache), select: filterCache.select })
              .pipe(
                switchMap((data) =>
                  this.updateUrlWithDigest(data.digest).pipe(
                    map(() => ({ filters: parseParams(filterCache), select: filterCache.select }))
                  )
                )
              )
          )
        );
    } else if (params.filterCacheType && digest) {
      return this.filterDigestService.findOne<T>(digest).pipe(
        tap((filterDigest) => {
          filterParams = filterDigest.filter?.['filters'];
        }),
        switchMap((filterDigest: FilterDigestFindInterface<T>) =>
          this.filterDigestService
            .createOne({
              filters: parseParams({ ...filterDigest.filter } as any),
              select: (filterDigest.filter as any)?.select
            })
            .pipe(
              switchMap((data) =>
                this.updateUrlWithDigest(data.digest).pipe(
                  map(() => ({
                    filters: parseParams({ ...filterDigest.filter } as any),
                    select: (filterDigest.filter as any)?.select
                  }))
                )
              )
            )
        )
      );
    } else if (!params.filterCacheType) {
      return of({ filters: parseParams() });
    }
  }

  public getUrlTreeWithDigest(
    commands: string[],
    queryParams: Record<string, any>,
    isStartSearch: boolean = false,
    select: string[] = null
  ): Observable<UrlTree> {
    return this.filterDigestService
      .createOne({
        filters: queryParams,
        select: select
      })
      .pipe(
        map((data: FilterDigestFindInterface) => {
          return this.router.createUrlTree(commands, {
            queryParams: flatten({
              digest: data.digest,
              isStartSearch: isStartSearch
            })
          });
        })
      );
  }

  private updateUrlWithDigest(digest: string) {
    return from(
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { digest },
        replaceUrl: true
      })
    ).pipe(
      catchError((error) => {
        console.error('Navigation failed:', error);
        return of(false);
      })
    );
  }

  public setQueryParamsV2(
    queryParams: Record<string, any>,
    params: {
      isNotSave?: boolean;
      filterCacheType?: FilterCacheKey;
      select?: string[];
      noScrollingToTop?: boolean;
      notSaveFields?: string[];
      notDeleteSort?: boolean;
    }
  ) {
    const filteredParams = flatten({ ...queryParams }) as Record<string, any>;
    const digest = unflatten(this.route.snapshot.queryParams)['digest'];

    delete filteredParams.limit;
    delete filteredParams.offset;
    delete filteredParams.search;
    delete filteredParams.filtersCount;

    if (!params.notDeleteSort) {
      delete filteredParams.sort;
    }

    Object.keys(filteredParams).forEach((key) => {
      if (
        filteredParams[key] == undefined ||
        Number.isNaN(filteredParams[key]) ||
        filteredParams[key] === [undefined] ||
        filteredParams[key] === '[object Object]' ||
        filteredParams[key] === '{}' ||
        (typeof filteredParams[key] === 'object' && !Object.keys(filteredParams[key]).length)
      ) {
        delete filteredParams[key];
      }
    });

    if (params?.filterCacheType && !params?.isNotSave) {
      const savedFilter = { ...queryParams };
      Object.keys(savedFilter).forEach((key) => {
        if (params?.notSaveFields?.includes(key)) {
          delete savedFilter[key];
        }
      });
      const filters = params?.notSaveFields?.length ? savedFilter : queryParams;
      const digestObs = digest
        ? this.filterDigestService.updateOne(digest, {
            filters,
            select: params?.select
          })
        : of([]);
      const defaultObs = this.filterCacheService.save(params?.filterCacheType, {
        filters,
        select: params?.select
      });
      const resObs = [defaultObs, digestObs];

      forkJoin(resObs).subscribe();
    }

    const currentScrollHeight = window.scrollY;

    this.router
      .navigate([], {
        relativeTo: this.route,
        queryParams: digest ? { digest } : null,
        replaceUrl: true
      })
      .then(() => {
        if (params?.noScrollingToTop) {
          window.scrollTo({ top: currentScrollHeight });
        }
      })
      .catch(console.error);
  }

  public getFilteredEntities<T>(filters: T, queryParams: Record<string, any>): T {
    const result = {} as T;

    Object.keys(paramsMap).forEach((key) => {
      const [fieldName, fieldType, fieldKey] = paramsMap[key].split(':');

      if (!(fieldName in filters)) {
        return;
      }

      if (fieldType === 'array') {
        if (queryParams[key] != undefined && !Array.isArray(queryParams[key])) {
          queryParams[key] = [queryParams[key]];
        }

        result[fieldName] =
          queryParams[key] && fieldKey
            ? filters[fieldName].filter((option) =>
                queryParams[key].map((param) => String(param)).includes(String(option[fieldKey]))
              )
            : queryParams[key] || [];
      } else if (fieldType === 'hierarchy') {
        if (queryParams[key] != undefined && !Array.isArray(queryParams[key])) {
          queryParams[key] = [queryParams[key]];
        }

        if (key === 'directionIds') {
          let listHierarchy = [];
          const parseChildren = (_options: any[]) => {
            _options.forEach((option) => {
              if (
                queryParams[key] &&
                queryParams[key].findIndex((item) => item?.toString() === String(option[fieldKey])) !== -1
              ) {
                listHierarchy.push(option);
              }
              if (option.children?.length) {
                parseChildren(option.children);
              }
            });
          };

          parseChildren(filters[fieldName]);
          result[fieldName] = listHierarchy;
        } else {
          let listHierarchy = [];
          const parseChildren = (_options: any[], parent?: any) => {
            _options.forEach((option) => {
              if (
                queryParams[key] &&
                queryParams[key].findIndex((si) => si?.toString() === String(option[fieldKey])) !== -1
              ) {
                listHierarchy.push(option);
              }
              if (option.children?.length) {
                parseChildren(option.children, option);
              }
            });
            if (
              parent?.children?.every(
                (child) => listHierarchy.findIndex((selectItem) => selectItem[fieldKey] === child[fieldKey]) !== -1
              ) &&
              listHierarchy.findIndex((selectItem) => selectItem[fieldKey] === parent?.[fieldKey]) === -1
            ) {
              listHierarchy.push(parent);
            }
            if (
              !parent?.children?.every(
                (child) => listHierarchy.findIndex((selectItem) => selectItem[fieldKey] === child[fieldKey]) !== -1
              ) &&
              listHierarchy.findIndex((selectItem) => selectItem[fieldKey] === parent?.[fieldKey]) !== -1
            ) {
              listHierarchy = listHierarchy.filter((selectItem) => selectItem[fieldKey] !== parent?.[fieldKey]);
            }
          };

          parseChildren(filters[fieldName]);
          result[fieldName] = listHierarchy;
        }
      }
    });

    return { ...result };
  }
}
